<template>
  <header class="navbar" :class="{ 'navbar--logo-hidden': logoHidden }">
    <div class="navbar__statusbar-bg-wrap">
      <div class="navbar__statusbar-bg" :style="'opacity: ' + backgroundOpacity"></div>
    </div>
    <div class="navbar__bg-wrap">
      <div class="navbar__bg" :style="'opacity: ' + backgroundOpacity">
        <HUIIconLogo></HUIIconLogo>
      </div>
    </div>
    <div ref="navbarInner" class="navbar__inner">
      <div class="navbar__left">
        <transition name="navbar__button--fade">
          <template v-if="$slots.left">
            <slot name="left"></slot>
          </template>
        </transition>
      </div>
      <div class="navbar__right">
        <transition name="navbar__button--fade">
          <template v-if="$slots.right">
            <slot name="right"></slot>
          </template>
        </transition>
      </div>
    </div>
    <div ref="staticLogo" class="navbar__logo-large">
      <transition name="navbar__logo-large-spinner-">
        <div class="navbar__logo-large-spinner" v-if="navbarLoading">
          <HUILoadingView inverted />
        </div>
      </transition>
      <HUIIconLogoDetailed></HUIIconLogoDetailed>
    </div>
  </header>
</template>

<script>
import { mapState } from 'vuex'
import { HUIIconLogoDetailed, HUIIconLogo, HUILoadingView } from '@/halifax-ui'

export default {
  components: {
    HUIIconLogo,
    HUIIconLogoDetailed,
    HUILoadingView
  },
  props: {
    logoHidden: Boolean
  },
  data() {
    return {
      backgroundOpacity: 0,
      scrollYToStartBackgroundFadeAt: null,
      distanceFromStartToEndBackgroundFadeAt: 25
    }
  },
  computed: {
    ...mapState({
      navbarLoading: 'navbar_loading'
    })
  },
  watch: {
    $route() {
      this.updateDockedBG()
    }
  },
  mounted() {
    const buffer = 50
    const largeLogoOffsetTop = this.$refs.staticLogo.offsetTop
    const largeLogoOffsetHeight = this.$refs.staticLogo.offsetHeight
    this.scrollYToStartBackgroundFadeAt = largeLogoOffsetTop + largeLogoOffsetHeight - 10

    this.fadeIO = new IntersectionObserver(this.handleFadeIOIntersecting, {
      rootMargin: `${this.scrollYToStartBackgroundFadeAt +
        this.distanceFromStartToEndBackgroundFadeAt +
        buffer}px 0px 0px 0px`,
      threshold: 0
    })
    this.fadeIO.observe(this.$el)
    this.pulledIO = new IntersectionObserver(this.handlePulledIOIntersecting, {
      rootMargin: `-${largeLogoOffsetHeight / 2}px 0px 0px 0px`,
      threshold: 0
    })
    this.pulledIO.observe(this.$el)
    this.$store.dispatch('SET_NAVBAR_HEIGHT', this.$refs.navbarInner.offsetHeight)
  },
  beforeDestroy() {
    this.fadeIO.disconnect()
    this.pulledIO.disconnect()
    window.removeEventListener('scroll', this.handleWindowScroll)
  },
  methods: {
    handlePulledIOIntersecting([entry]) {
      if (entry.isIntersecting) {
        this.$bus.$emit('NAVBAR_PULLED_DOWN')
      }
    },
    handleFadeIOIntersecting([entry]) {
      if (entry.isIntersecting) {
        window.addEventListener('scroll', this.handleWindowScroll)
      } else {
        window.removeEventListener('scroll', this.handleWindowScroll)
      }
      this.handleWindowScroll()
    },
    updateDockedBG() {
      const scrollY = window.scrollY
      const startScrollY = this.scrollYToStartBackgroundFadeAt
      const maxDistanceFromStart = this.distanceFromStartToEndBackgroundFadeAt
      const distanceFromStart = Math.min(Math.max(scrollY - startScrollY, 0), maxDistanceFromStart)

      this.backgroundOpacity = Math.min(distanceFromStart / maxDistanceFromStart, 0.999999)
    },
    handleWindowScroll() {
      if (this.logoHidden) {
        return
      }

      this.updateDockedBG()
    }
  }
}
</script>

<style lang="scss">
@import '~@/halifax-ui/src/scss/framework';
@import '~@/scss/variables';

.navbar {
  $module: '.navbar';
  $border-width: 2px;
  $small-logo-size: 33px;
  $bg-color: $background-color;
  $docked-bg-color: #ddd6bf;

  position: relative;
  height: 0;

  // Hide if framed
  .window--framed & {
    display: none;
  }
  &__inner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    height: $height-navbar;
    box-sizing: content-box;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    z-index: $z-index-navbar;

    @include supports-safe-area-insets {
      padding-top: var(--safe-area-inset-top);
    }
  }
  &__left,
  &__right {
    position: relative;
    box-sizing: border-box;
    height: $height-navbar - 6px * 2;
    margin: 6px;
    display: flex;
    align-items: center;
    flex: 1;

    > * {
      position: absolute;
    }
  }
  &__left {
    margin-right: auto;
    justify-content: flex-start;
  }
  &__right {
    margin-left: auto;
    justify-content: flex-end;
  }
  &__logo-large-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 36px;
    height: 36px;
    margin-top: -(36px / 2);
    margin-left: -(36px / 2);
    background: $text-color;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;

    &--enter-active,
    &--leave-active {
      transition: opacity 200ms;
    }
    &--enter,
    &--leave-to {
      opacity: 0;
    }
  }
  &__logo-large {
    position: absolute;
    left: 50%;
    top: $height-navbar - $border-width / 2;
    width: $navbar-large-logo-size;
    height: $navbar-large-logo-size;
    margin-top: -($navbar-large-logo-size / 2);
    margin-left: -($navbar-large-logo-size / 2);
    transform: translate3d(0, 0, 0);
    z-index: $z-index-navbar;
    padding: $navbar-large-logo-border-size;
    background: $background-color;
    border-radius: 100px;

    #{$module}--logo-hidden & {
      opacity: 0 !important;
      pointer-events: none;
    }
    @include supports-safe-area-insets {
      transform: translate3d(0, var(--safe-area-inset-top), 0);
    }
    svg {
      width: $navbar-large-logo-size - $navbar-large-logo-border-size * 2;
      height: $navbar-large-logo-size - $navbar-large-logo-border-size * 2;
      display: block;
      background-color: $background-light-70;
      border-radius: 1000px;
    }
  }
  &__statusbar-bg-wrap {
    pointer-events: none;
    position: fixed;
    z-index: $z-index-navbar + 1;
    top: 0;
    left: 0;
    width: 100%;
    background-color: rgba($background-color, 0.95);
    display: none;

    @include supports-safe-area-insets {
      display: block;
      height: var(--safe-area-inset-top);

      // Hide on iPhone X
      @media only screen and (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) {
        display: none;
      }
    }
  }
  &__statusbar-bg {
    height: 100%;
    width: 100%;
    background-color: $docked-bg-color;
    will-change: opacity;

    #{$module}--logo-hidden & {
      opacity: 1 !important;
      transition: opacity 50ms linear;
    }
  }
  &__bg-wrap {
    pointer-events: none;
    position: fixed;
    z-index: $z-index-navbar;
    top: 0;
    left: 0;
    width: 100%;
    background-color: $background-color;
    border-bottom: $border-width solid $border-color-navbar;
    box-sizing: content-box;
  }
  &__bg {
    width: 100%;
    height: $height-navbar - $border-width;
    background-color: $docked-bg-color;
    will-change: opacity;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: content-box;

    #{$module}--logo-hidden & {
      opacity: 1 !important;
      transition: opacity 50ms linear;
    }
    @include supports-safe-area-insets {
      padding-top: var(--safe-area-inset-top);
    }
    svg {
      height: $small-logo-size;
      width: $small-logo-size;
      background-color: $background-light-50;
      border-radius: 10000px;
    }
  }
  &__button--fade-enter-active,
  &__button--fade-leave-active {
    transition: opacity 50ms linear;
  }
  &__button--fade-enter,
  &__button--fade-leave-to {
    opacity: 0;
  }
}
</style>
